import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { cn } from '@/lib/utils'; // Keep if needed, or remove if unused

const BrandingManager = () => {
    const [branding, setBranding] = useState({
        logo: '',
        favicon: '',
        logoFile: null,
        faviconFile: null
    });
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');

    useEffect(() => {
        const fetchBranding = async () => {
            try {
                const { data } = await api.get('/sections/Branding');
                if (data && data.content) {
                    const contentMap = {};
                    if (Array.isArray(data.content)) {
                        data.content.forEach(item => {
                            contentMap[item.key] = item.value;
                        });
                        setBranding(prev => ({ ...prev, ...contentMap }));
                    }
                }
                setLoading(false);
            } catch (error) {
                console.error(error);
                setLoading(false);
            }
        };

        fetchBranding();
    }, []);

    const handleFileChange = (e, type) => {
        const file = e.target.files[0];
        if (file) {
            setBranding(prev => ({
                ...prev,
                [type + 'File']: file,
                [type]: URL.createObjectURL(file)
            }));
        }
    };

    const getFullUrl = (path) => {
        if (!path) return '';
        if (path.startsWith('blob:') || path.startsWith('http')) return path;
        return `${import.meta.env.VITE_API_URL.replace('/api', '')}${path}`;
    };

    const handleSave = async () => {
        try {
            let logoUrl = branding.logo;
            let faviconUrl = branding.favicon;

            // Upload Logo if changed and file exists
            if (branding.logoFile) {
                const logoFormData = new FormData();
                logoFormData.append('file', branding.logoFile);
                const { data } = await api.post('/upload', logoFormData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                logoUrl = data.path;
            }

            // Upload Favicon if changed and file exists
            if (branding.faviconFile) {
                const faviconFormData = new FormData();
                faviconFormData.append('file', branding.faviconFile);
                const { data } = await api.post('/upload', faviconFormData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                faviconUrl = data.path;
            }

            // Save to Section content
            const contentArray = [
                { key: 'logo', type: 'image', value: logoUrl },
                { key: 'favicon', type: 'image', value: faviconUrl }
            ];

            await api.put('/sections/Branding', { content: contentArray });

            // Update state with new URLs (and clear files to avoid re-upload)
            setBranding(prev => ({
                ...prev,
                logo: logoUrl,
                favicon: faviconUrl,
                logoFile: null,
                faviconFile: null
            }));

            setMessage('Branding updated successfully!');
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            console.error('Error saving branding:', error);
            setMessage('Error saving branding settings.');
        }
    };

    if (loading) return <div>Loading...</div>;

    return (
        <div className="max-w-4xl mx-auto bg-white p-8 rounded shadow text-gray-900">
            <h2 className="text-2xl font-bold mb-6">Branding Manager</h2>
            {message && <div className={`p-4 mb-4 rounded ${message.includes('Error') ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>{message}</div>}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                    <h3 className="font-semibold mb-4">Logo</h3>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 flex flex-col items-center justify-center min-h-[200px]">
                        {branding.logo ? (
                            <img src={getFullUrl(branding.logo)} alt="Logo" className="max-h-32 mb-4 object-contain" />
                        ) : (
                            <div className="text-gray-400 mb-4">No Logo Uploaded</div>
                        )}
                        <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleFileChange(e, 'logo')}
                            className="block w-full text-sm text-gray-500
                                file:mr-4 file:py-2 file:px-4
                                file:rounded-full file:border-0
                                file:text-sm file:font-semibold
                                file:bg-blue-50 file:text-blue-700
                                hover:file:bg-blue-100"
                        />
                    </div>
                </div>

                <div>
                    <h3 className="font-semibold mb-4">Favicon</h3>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 flex flex-col items-center justify-center min-h-[200px]">
                        {branding.favicon ? (
                            <img src={getFullUrl(branding.favicon)} alt="Favicon" className="w-16 h-16 mb-4 object-contain" />
                        ) : (
                            <div className="text-gray-400 mb-4">No Favicon</div>
                        )}
                        <input
                            type="file"
                            accept="image/*" // .ico, .png
                            onChange={(e) => handleFileChange(e, 'favicon')}
                            className="block w-full text-sm text-gray-500
                                file:mr-4 file:py-2 file:px-4
                                file:rounded-full file:border-0
                                file:text-sm file:font-semibold
                                file:bg-blue-50 file:text-blue-700
                                hover:file:bg-blue-100"
                        />
                    </div>
                </div>
            </div>

            <button onClick={handleSave} className="mt-8 px-6 py-2 bg-black text-white rounded hover:bg-gray-800">
                Save Changes
            </button>
        </div>
    );
};

export default BrandingManager;
